import { useConfirm } from '@euler/functions/ux/useConfirm';
import { useCreation } from '@euler/utils/hooks';
import { useCallback, useEffect, useRef } from 'react';
import { Subject, exhaustMap, filter, from, of, switchMap } from 'rxjs';
import { PosterStudioCloseAnswer } from './usePosterStudioSession';

/**
 * Leaving the studio: ask it first, and ask the shop before dropping unsaved
 * work. A back pressed again while one is being answered is ignored, and
 * nothing leaves once the page has gone.
 */
export function usePosterStudioExit({
  requestClose,
  onLeave,
}: {
  requestClose: () => Promise<PosterStudioCloseAnswer>;
  onLeave: () => void;
}) {
  const confirm = useConfirm();
  const exits$ = useCreation(() => new Subject<void>(), []);
  const latest = useRef({ requestClose, onLeave, confirm });
  latest.current = { requestClose, onLeave, confirm };

  useEffect(() => {
    const subscription = exits$
      .pipe(
        exhaustMap(() =>
          from(latest.current.requestClose()).pipe(
            switchMap(answer =>
              answer.kind === 'close'
                ? of(true)
                : from(
                    latest.current.confirm({
                      title: '确定离开吗？',
                      message: answer.message,
                      confirmButtonText: '离开',
                      destructive: true,
                    }),
                  ),
            ),
          ),
        ),
        filter(leave => leave),
      )
      .subscribe(() => latest.current.onLeave());
    return () => subscription.unsubscribe();
  }, [exits$]);

  return useCallback(() => exits$.next(), [exits$]);
}
